/*
 * Copyright (c) 2026. World Wide Generation Ltd
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { formatDateUTC, DATE } from '@utils/date';
import { Badge, Collapse } from 'reactstrap';
import Dashboard, { DashboardSection, DashboardSectionTitle } from '@g17eco/molecules/dashboard';
import { QueryWrapper } from '@g17eco/molecules/query/QueryWrapper';
import { FeatureStability } from '@g17eco/molecules/feature-stability';
import { SearchBox } from '@g17eco/molecules/search/SearchBox';
import { toastFromError } from '@g17eco/molecules/toasts';
import { ConfirmationModal } from '@g17eco/molecules/confirm-modal/ConfirmationModal';
import { SectionLoader } from '@g17eco/atoms/loader/SectionLoader';
import ButtonWithLoader from '@g17eco/molecules/button/ButtonWithLoader';
import {
  useGetDataTransferDashboardQuery,
  useGetDataTransferSubsidiaryJobsQuery,
  useGetDataTransferUtrsQuery,
  useListSubConnectionsQuery,
  useSubmitDataTransferReviewMutation,
  useTriggerDataTransferSyncMutation,
  useTriggerDataTransferSyncTaskMutation,
  useRegenerateDataTransferPendingMutation,
  useCompleteDataTransferJobMutation,
  useReopenDataTransferJobMutation,
  useRetryDataTransferSyncMutation,
} from '@api/initiative-app-integrations';
import './DataTransferReviewPage.scss';
import { useAppSelector } from '@reducers/index';
import { isStaff } from '@selectors/user';
import { CTAdminBreadcrumbs } from '../../../../components/breadcrumbs/CTAdminBreadcrumbs';
import { ROUTES } from '@constants/routes';
import { QUESTION } from '@constants/terminology';
import { generateUrl } from '@routes/util';
import { JobStatus, TaskStatus, TaskType } from '@g17eco/types/background-jobs';
import { RowApprovalStatus, type DataTransferJob, type DataTransferTask } from '@g17eco/types/data-transfer';
import type { UniversalTrackerPlain } from '@g17eco/types/universalTracker';
import type { ValueListPlain } from '@g17eco/types/valueList';
import { StatusBadge, getRowBorderColor } from '@g17eco/atoms/status-badge';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { renderNoticeAlerts } from '../data-transfer-utils';
import {
  isMetricTask, resolveTaskApproval, matchesFilter, formatValue, formatUtrCode,
  getGriSection, getSubsidiaryLabel, isNumericValueType, ACTIONABLE, type ReviewFilter,
  isRefreshStuck, resolveEntryLabel, getGenerateTaskNotices,
} from './utils';
import { TruncatedValue, DiffCell, DiffBadge, ProvenanceModalForMetric } from './DataTransferReviewComponents';


const FILTER_OPTIONS: Array<{ value: ReviewFilter; label: string }> = [
  { value: 'actionable', label: 'Actionable' },
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'skipped', label: 'Skipped' },
];

interface FlatRow {
  task: DataTransferTask;
  jobId: string;
  periodLabel: string;
  approval: RowApprovalStatus;
}

// Narrow an RTK Query mutation result discriminated union and toast on error.
// Returns true on success so callers can early-return on failure.
const handleMutationResult = <T,>(result: { data: T } | { error: unknown }, title: string): result is { data: T } => {
  if ('error' in result) {
    toastFromError({ error: result.error, title });
    return false;
  }
  return true;
};

export const DataTransferReviewPage = () => {
  const { code, initiativeId, dataTransferId, subsidiaryId } = useParams<{
    code: string; initiativeId: string; dataTransferId: string; subsidiaryId: string;
  }>();
  const isUserStaff = useAppSelector(isStaff);
  const [filter, setFilter] = useState<ReviewFilter>('actionable');
  const [search, setSearch] = useState('');
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [collapsedPeriods, setCollapsedPeriods] = useState<Set<string>>(new Set());
  const [localApprovals, setLocalApprovals] = useState<Map<string, RowApprovalStatus>>(new Map());
  const [refreshConfirm, setRefreshConfirm] = useState<{ jobId: string; period: string; hasSynced: boolean } | null>(null);
  const [provenanceUtrvId, setProvenanceUtrvId] = useState<{ utrvId: string; utrCode: string } | null>(null);
  const [submitReview, { isLoading: isSubmitting }] = useSubmitDataTransferReviewMutation();
  const [triggerSync, { isLoading: isSyncing }] = useTriggerDataTransferSyncMutation();
  const [triggerSyncTask] = useTriggerDataTransferSyncTaskMutation();
  const [regeneratePending] = useRegenerateDataTransferPendingMutation();
  const [completeJob] = useCompleteDataTransferJobMutation();
  const [reopenJob] = useReopenDataTransferJobMutation();
  const [retrySync] = useRetryDataTransferSyncMutation();

  const dashboardQuery = useGetDataTransferDashboardQuery(
    isUserStaff && dataTransferId ? { initiativeId, code, dataTransferId } : skipToken,
  );
  const dataTransferName = dashboardQuery.data?.dataTransfer.name;

  const query = useGetDataTransferSubsidiaryJobsQuery(
    isUserStaff && dataTransferId && subsidiaryId ? { initiativeId, code, dataTransferId, subsidiaryId } : skipToken,
    { pollingInterval: 10000, refetchOnMountOrArgChange: true },
  );

  const subConnectionsQuery = useListSubConnectionsQuery(
    isUserStaff ? { initiativeId, code } : skipToken,
  );

  const utrsQuery = useGetDataTransferUtrsQuery(
    isUserStaff ? { initiativeId, code } : skipToken,
  );

  const subsidiaryName = useMemo(() => {
    const sc = subConnectionsQuery.data?.find((s) => s.initiativeId === subsidiaryId);
    return sc ? getSubsidiaryLabel(sc) : subsidiaryId;
  }, [subConnectionsQuery.data, subsidiaryId]);

  const utrByCode = useMemo(() => {
    const map = new Map<string, UniversalTrackerPlain>();
    for (const utr of utrsQuery.data?.utrs ?? []) {
      map.set(utr.code, utr);
    }
    return map;
  }, [utrsQuery.data]);

  const valueListById = useMemo(() => {
    const map = new Map<string, ValueListPlain>();
    for (const vl of utrsQuery.data?.valueLists ?? []) {
      map.set(vl._id, vl);
    }
    return map;
  }, [utrsQuery.data]);

  const getEntryLabel = useCallback(
    (utrCode: string, optionCode?: string) => resolveEntryLabel(utrCode, optionCode, utrByCode, valueListById),
    [utrByCode, valueListById],
  );

  const getUtrName = useCallback((utrCode: string): string | undefined => {
    return utrByCode.get(utrCode)?.name;
  }, [utrByCode]);

  const togglePeriod = useCallback((period: string) => {
    setCollapsedPeriods((prev) => {
      const next = new Set(prev);
      if (next.has(period)) {
        next.delete(period);
      } else {
        next.add(period);
      }
      return next;
    });
  }, []);

  const toggleExpand = useCallback((taskId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  }, []);

  const toggleSelection = useCallback((taskId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  }, []);

  const handleBulkApproval = useCallback((rows: FlatRow[], target: RowApprovalStatus) => {
    const selected = rows.filter((r) => selectedIds.has(r.task.id));
    if (selected.length === 0) {
      return;
    }

    const updates = new Map(localApprovals);
    for (const row of selected) {
      updates.set(row.task.id, target);
    }
    setLocalApprovals(updates);

    // Save all to API
    Promise.allSettled(
      selected.map((row) =>
        submitReview({ initiativeId, code, dataTransferId, jobId: row.jobId, taskId: row.task.id, approval: target }).unwrap(),
      ),
    ).then((results) => {
      const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
      if (failures.length > 0) {
        toastFromError({ error: failures[0].reason, title: `${failures.length} of ${selected.length} reviews failed to save` });
      }
    });

    setSelectedIds(new Set());
  }, [selectedIds, localApprovals, submitReview, initiativeId, code, dataTransferId]);

  const handleRowAction = useCallback((taskId: string, jobId: string, target: RowApprovalStatus, current: RowApprovalStatus) => {
    if (target === current) {
      return;
    }
    setLocalApprovals((prev) => new Map(prev).set(taskId, target));
    submitReview({ initiativeId, code, dataTransferId, jobId, taskId, approval: target }).unwrap().catch((error: unknown) => {
      toastFromError({ error, title: 'Failed to save review' });
      setLocalApprovals((prev) => {
        const reverted = new Map(prev);
        reverted.delete(taskId);
        return reverted;
      });
    });
  }, [submitReview, initiativeId, code, dataTransferId]);

  // Escape key exits select mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectMode) {
        setSelectMode(false);
        setSelectedIds(new Set());
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectMode]);

  const handleSubmitAndSync = useCallback(async (jobs: DataTransferJob[]) => {
    for (const job of jobs) {
      const result = await triggerSync({ initiativeId, code, dataTransferId, jobId: job._id });
      if (!handleMutationResult(result, 'Failed to trigger sync')) {
        return;
      }
    }
  }, [triggerSync, initiativeId, code, dataTransferId]);

  const handleSyncPeriod = useCallback(async (jobId: string) => {
    const result = await triggerSync({ initiativeId, code, dataTransferId, jobId });
    handleMutationResult(result, 'Failed to trigger sync');
  }, [triggerSync, initiativeId, code, dataTransferId]);

  const handleSyncTask = useCallback(async (taskId: string, jobId: string) => {
    if (localApprovals.has(taskId)) {
      const approval = localApprovals.get(taskId);
      if (approval) {
        const reviewResult = await submitReview({ initiativeId, code, dataTransferId, jobId, taskId, approval });
        if (!handleMutationResult(reviewResult, 'Failed to save review before sync')) {
          return;
        }
        setLocalApprovals((prev) => {
          const next = new Map(prev);
          next.delete(taskId);
          return next;
        });
      }
    }
    const result = await triggerSyncTask({ initiativeId, code, dataTransferId, jobId, taskId });
    handleMutationResult(result, 'Failed to trigger sync');
  }, [localApprovals, submitReview, triggerSyncTask, initiativeId, code, dataTransferId]);

  if (!isUserStaff) {
    return null;
  }

  return (
    <Dashboard size='xl'>
      <div className='pl-2 pb-4'>
        <CTAdminBreadcrumbs
          initiativeId={initiativeId}
          breadcrumbs={[
            { label: 'Connections', url: generateUrl(ROUTES.APP_INTEGRATIONS, { initiativeId }) },
            { label: code, url: generateUrl(ROUTES.APP_INTEGRATIONS_VIEW, { initiativeId, code }) },
            { label: 'Data Transfers', url: generateUrl(ROUTES.APP_INTEGRATIONS_DATA_TRANSFERS, { initiativeId, code }) },
            { label: dataTransferName ?? 'Dashboard', url: generateUrl(ROUTES.APP_INTEGRATIONS_DATA_TRANSFER_SYNC, { initiativeId, code, dataTransferId }) },
            { label: subsidiaryName },
          ]}
        />
      </div>
      <QueryWrapper query={query} onFetching={() => <></>} onSuccess={(jobs) => {
        if (jobs.length === 0) {
          return (
            <DashboardSection>
              <div className='text-center py-5 text-ThemeTextLight'>No jobs found for this subsidiary.</div>
            </DashboardSection>
          );
        }

        const matchesFilterAndSearch = (r: FlatRow) => {
          if (!matchesFilter(r.approval, filter)) {
            return false;
          }
          if (search && !r.task.data.utrCode.toLowerCase().includes(search.toLowerCase())) {
            return false;
          }
          return true;
        };

        // Build per-job (period) data
        const jobPeriods = jobs.map((job) => {
          const generateTask = job.tasks.find((t) => t.type === TaskType.DataTransferGenerate);
          const taskData = generateTask?.data as { startDate?: string } | undefined;
          const periodLabel = taskData?.startDate
            ? formatDateUTC(taskData.startDate, DATE.MONTH_YEAR_SHORT)
            : 'Unknown';
          const allRows: FlatRow[] = job.tasks.filter(isMetricTask).map((task) => {
            const saved = resolveTaskApproval(task.data);
            const approval = localApprovals.get(task.id) ?? saved;
            return { task, jobId: job._id, periodLabel, approval };
          });
          const filteredRows = allRows.filter(matchesFilterAndSearch);
          return { job, periodLabel, allRows, filteredRows };
        });

        const flatRows = jobPeriods.flatMap((p) => p.allRows);
        const filteredRows = jobPeriods.flatMap((p) => p.filteredRows);

        const counts = flatRows.reduce(
          (acc, r) => {
            switch (r.approval) {
              case RowApprovalStatus.Pending: acc.pending++; break;
              case RowApprovalStatus.Approved: acc.approved++; break;
              case RowApprovalStatus.Rejected: acc.rejected++; break;
              case RowApprovalStatus.Skipped:
              case RowApprovalStatus.Flagged: acc.skipped++; break;
            }
            return acc;
          },
          { pending: 0, approved: 0, rejected: 0, skipped: 0 },
        );
        const total = flatRows.length;
        // Completed periods are locked server-side — firing triggerSync against
        // them returns 500. Exclude their rows from the "Sync All N" counter so
        // users aren't offered an action that will immediately fail.
        const completedJobIds = new Set(jobs.filter((j) => j.status === JobStatus.Completed).map((j) => j._id));
        const anyBulkSyncing = jobs.some(
          (j) => j.tasks.some((t) => t.type === TaskType.DataTransferBulkSync && t.status === TaskStatus.Pending),
        );
        const syncableJobs = jobs.filter(
          (j) => !completedJobIds.has(j._id)
            && !j.tasks.some((t) => t.type === TaskType.DataTransferBulkSync && t.status === TaskStatus.Pending),
        );
        const totalApprovedUnsynced = flatRows.filter(
          (r) => r.approval === RowApprovalStatus.Approved && !r.task.data.sync && !completedJobIds.has(r.jobId),
        ).length;
        const processed = total > 0 ? Math.round(((counts.approved + counts.rejected + counts.skipped) / total) * 100) : 0;

        return (
          <>
            <DashboardSectionTitle
              title={<>Data Review <FeatureStability stability='internal' /></>}
              subtitle={`${subsidiaryName} · ${jobs.length} periods`}
              buttons={[
                <div key='counts' className='d-inline-flex align-items-center gap-3 small'>
                  <span className='d-inline-flex align-items-center gap-1 text-ThemeInfoMedium'><i className='fa fa-circle' style={{ fontSize: 8 }} /> {counts.pending} Pending</span>
                  <span className='d-inline-flex align-items-center gap-1 text-ThemeSuccessMedium'><i className='fa fa-circle' style={{ fontSize: 8 }} /> {counts.approved} Approved</span>
                  <span className='d-inline-flex align-items-center gap-1 text-ThemeDangerMedium'><i className='fa fa-circle' style={{ fontSize: 8 }} /> {counts.rejected} Rejected</span>
                  <span className='d-inline-flex align-items-center gap-1 text-ThemeTextLight'><i className='fa fa-circle' style={{ fontSize: 8 }} /> {counts.skipped} Skipped</span>
                  <span className='fw-bold'>{processed}% reviewed</span>
                </div>,
              ]}
            />


            <div className='dashboard-row d-flex flex-wrap align-items-center gap-3 mb-3'>
              <div style={{ minWidth: 280, maxWidth: 400, flex: '1 1 auto' }}>
                <SearchBox
                  searchText={search}
                  handleOnChange={(e) => setSearch(e.target.value)}
                  placeholder={`Search by ${QUESTION.SINGULAR} code...`}
                  classNames={{ input: 'py-2' }}
                />
              </div>
              <div className='d-inline-flex align-items-center border rounded p-1 gap-1 background-ThemeTextWhite'>
                {FILTER_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    className={`btn btn-sm border-0 px-2 py-1 ${filter === opt.value ? 'btn-primary' : 'text-ThemeTextMedium'}`}
                    onClick={() => setFilter(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <span className='small text-ThemeTextLight text-nowrap'>{filteredRows.length} of {total} rows</span>
              <button
                className={`btn btn-sm border-0 d-flex align-items-center gap-1 ${selectMode ? 'text-ThemeDangerMedium' : 'text-ThemeTextMedium'}`}
                onClick={() => { setSelectMode(!selectMode); setSelectedIds(new Set()); }}
              >
                {selectMode ? (
                  <><i className='fal fa-times' /> Cancel</>
                ) : (
                  <><i className='fal fa-ballot-check' /> Select</>
                )}
              </button>
              {totalApprovedUnsynced > 0 && (
                <ButtonWithLoader
                  className='ms-md-auto text-nowrap'
                  color='primary'
                  loading={isSyncing || isSubmitting || anyBulkSyncing}
                  onClick={() => handleSubmitAndSync(syncableJobs)}
                >
                  <i className='fa fa-cloud-upload me-1' />
                  {anyBulkSyncing ? 'Syncing...' : `Sync All ${totalApprovedUnsynced} Approved`}
                </ButtonWithLoader>
              )}
            </div>


            {jobPeriods.map(({ job: periodJob, periodLabel: period, allRows, filteredRows: rows }) => {
              const periodJobId = periodJob._id;
              const isCompleted = periodJob.status === JobStatus.Completed;
              const generateTask = periodJob.tasks.find((t) => t.type === TaskType.DataTransferGenerate);
              const isProcessing = generateTask?.status === TaskStatus.Pending && !isCompleted;
              const isBulkSyncing = periodJob.tasks.some(
                (t) => t.type === TaskType.DataTransferBulkSync && t.status === TaskStatus.Pending,
              );
              const isStuck = isRefreshStuck(periodJob);
              const periodNotices = isProcessing ? [] : getGenerateTaskNotices(periodJob);
              const isCollapsed = isCompleted ? !collapsedPeriods.has(period) : collapsedPeriods.has(period);
              const periodPending = allRows.filter((r) => r.approval === RowApprovalStatus.Pending).length;
              const periodSkipped = allRows.filter((r) => r.approval === RowApprovalStatus.Skipped).length;
              const allNoSurvey = allRows.length > 0 && allRows.every((r) => r.task.data.surveyStatus === 'no_survey');
              const periodErrors = allRows.filter((r) => r.task.data.sync?.status === 'error').length;
              const periodApprovedUnsynced = allRows.filter((r) => r.approval === RowApprovalStatus.Approved && !r.task.data.sync).length;
              const periodSurveyId = allRows.find((r) => r.task.data.surveyId)?.task.data.surveyId;
              const surveyUrl = periodSurveyId
                ? generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, { initiativeId: subsidiaryId, surveyId: periodSurveyId })
                : undefined;
              return (
                <React.Fragment key={periodJob._id}>
                  <DashboardSection padding={0} paddingInternal={0}>

                    <div
                      className='d-flex flex-wrap align-items-center gap-2 px-4 py-3 cursor-pointer'
                      onClick={() => togglePeriod(period)}
                    >
                      <i className={`fal fa-chevron-${isCollapsed ? 'right' : 'down'} text-ThemeTextMedium`} />
                      <span className='fw-bold'>{period} Report Period</span>
                      <Badge color='secondary' pill className='fw-normal'>{allRows.length} {allRows.length !== 1 ? QUESTION.PLURAL : QUESTION.SINGULAR}</Badge>
                      {periodPending > 0 && (
                        <Badge color='warning' pill className='fw-normal'>{periodPending} pending review</Badge>
                      )}
                      {allNoSurvey && (
                        <Badge color='secondary' pill className='fw-normal'>No Report</Badge>
                      )}
                      {!allNoSurvey && periodSkipped === allRows.length && (
                        <Badge color='secondary' pill className='fw-normal'>All skipped</Badge>
                      )}
                      {isProcessing && !isStuck && (
                        <Badge color='info' pill className='fw-normal'><i className='fas fa-spinner fa-spin me-1' />Refreshing</Badge>
                      )}
                      {isBulkSyncing && !isStuck && (
                        <Badge color='info' pill className='fw-normal'><i className='fas fa-spinner fa-spin me-1' />Syncing</Badge>
                      )}
                      {isStuck && (
                        <Badge color='warning' pill className='fw-normal'><i className='fal fa-exclamation-triangle me-1' />Stuck</Badge>
                      )}
                      {isCompleted && (
                        <Badge color='success' pill className='fw-normal'><i className='fal fa-check me-1' />Completed</Badge>
                      )}
                      {periodNotices.length > 0 && (
                        <Badge color='warning' pill className='fw-normal'>
                          <i className='fal fa-exclamation-triangle me-1' />
                          {periodNotices.length} {periodNotices.length === 1 ? 'notice' : 'notices'}
                        </Badge>
                      )}
                      {periodErrors > 0 && (
                        <Badge color='danger' pill className='fw-normal'>
                          <i className='fal fa-exclamation-triangle me-1' />
                          {periodErrors} error{periodErrors !== 1 ? 's' : ''}
                        </Badge>
                      )}
                      <div className='d-flex flex-wrap align-items-center gap-2 ms-md-auto'>
                        {periodJobId && !isCompleted && (
                          <button
                            className='btn btn-sm btn-outline-warning'
                            title={isProcessing ? 'Already refreshing — please wait' : 'Refresh snapshot data'}
                            disabled={isProcessing}
                            onClick={(e) => {
                              e.stopPropagation();
                              setRefreshConfirm({
                                jobId: periodJobId,
                                period,
                                hasSynced: allRows.some((r) => r.task.data.sync?.status === 'synced'),
                              });
                            }}
                          >
                            <i className='fal fa-refresh me-1' />Refresh
                          </button>
                        )}
                        {periodJobId && periodErrors > 0 && !isCompleted && (
                          <button
                            className='btn btn-sm btn-outline-danger'
                            title={`Retry ${periodErrors} failed sync${periodErrors !== 1 ? 's' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              retrySync({ initiativeId, code, dataTransferId, jobId: periodJobId }).then((r) => handleMutationResult(r, 'Failed to retry sync'));
                            }}
                          >
                            <i className='fal fa-redo me-1' />{periodErrors} Failed — Retry
                          </button>
                        )}
                        {periodJobId && periodApprovedUnsynced > 0 && !isCompleted && (
                          <button
                            className='btn btn-sm btn-outline-primary'
                            title={isBulkSyncing
                              ? 'Already syncing — please wait'
                              : `Sync ${periodApprovedUnsynced} approved ${periodApprovedUnsynced !== 1 ? QUESTION.PLURAL : QUESTION.SINGULAR} for this period`}
                            disabled={isSyncing || isSubmitting || isBulkSyncing}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSyncPeriod(periodJobId);
                            }}
                          >
                            <i className='fa fa-cloud-upload me-1' />Sync {periodApprovedUnsynced} Approved
                          </button>
                        )}
                        {periodJobId && (
                          <button
                            className={`btn btn-sm ${isCompleted ? 'btn-outline-secondary' : 'btn-outline-success'}`}
                            title={isCompleted ? 'Reopen this period' : 'Mark as complete'}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isCompleted) {
                                reopenJob({ initiativeId, code, dataTransferId, jobId: periodJobId }).then((r) => handleMutationResult(r, 'Failed to reopen job'));
                              } else {
                                completeJob({ initiativeId, code, dataTransferId, jobId: periodJobId }).then((r) => handleMutationResult(r, 'Failed to complete job'));
                              }
                            }}
                          >
                            <i className={`fal ${isCompleted ? 'fa-lock-open' : 'fa-lock'} me-1`} />
                            {isCompleted ? 'Reopen' : 'Complete'}
                          </button>
                        )}
                        {surveyUrl && (
                          <Link
                            to={surveyUrl}
                            className='btn btn-sm btn-outline-secondary'
                            onClick={(e) => e.stopPropagation()}
                          >
                            <i className='fal fa-external-link me-1' />View Report
                          </Link>
                        )}
                      </div>
                    </div>


                    <Collapse isOpen={!isCollapsed}>
                    {renderNoticeAlerts(periodNotices, 'mx-4 mt-3')}
                    {isProcessing && !isStuck && (
                      <div className='d-flex align-items-center justify-content-center' style={{ minHeight: 200 }}>
                        <SectionLoader />
                      </div>
                    )}
                    {isStuck && periodJobId && (
                      <div className='d-flex flex-column align-items-center justify-content-center gap-3 py-5 text-ThemeTextLight'>
                        <i className='fal fa-exclamation-triangle fa-2x text-ThemeWarningMedium' />
                        <span>This refresh appears to be stuck. You can retry it.</span>
                        <button
                          className='btn btn-sm btn-outline-warning'
                          onClick={() => {
                            regeneratePending({ initiativeId, code, dataTransferId, jobId: periodJobId })
                              .then((r) => handleMutationResult(r, 'Failed to retry refresh'));
                          }}
                        >
                          <i className='fal fa-refresh me-1' />Retry Refresh
                        </button>
                      </div>
                    )}
                    {!isProcessing && rows.length === 0 && (
                      <div className='text-center py-4 text-ThemeTextLight small'>
                        {allNoSurvey
                          ? `No report in scope for ${period}.`
                          : `All ${allRows.length} ${QUESTION.PLURAL} were skipped${periodNotices.length > 0 ? '.' : ' — no discrepancies found.'}`}
                      </div>
                    )}
                    {!isProcessing && rows.length > 0 && (
                      <table className='table mb-0'>
                        <thead>
                          <tr className='text-uppercase small text-ThemeTextMedium'>
                            {selectMode && (
                              <th className='ps-4 py-3' style={{ width: 48 }}>
                                <input
                                  type='checkbox'
                                  checked={rows.filter((r) => ACTIONABLE.has(r.approval) && !r.task.data.sync).every((r) => selectedIds.has(r.task.id))}
                                  onChange={(e) => {
                                    const actionableIds = rows
                                      .filter((r) => ACTIONABLE.has(r.approval) && !r.task.data.sync)
                                      .map((r) => r.task.id);
                                    setSelectedIds((prev) => {
                                      const next = new Set(prev);
                                      if (e.target.checked) {
                                        actionableIds.forEach((id) => next.add(id));
                                      } else {
                                        actionableIds.forEach((id) => next.delete(id));
                                      }
                                      return next;
                                    });
                                  }}
                                />
                              </th>
                            )}
                            <th className='py-3'>{QUESTION.CAPITALIZED_SINGULAR} / Indicator</th>
                            <th className='py-3 text-end'>Report Value</th>
                            <th className='py-3 text-end text-primary'>Proposed Value</th>
                            <th className='py-3 text-end'>Diff</th>
                            <th className='py-3 text-end'>Status</th>
                            <th className='py-3 text-end'>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row) => {
                            const { task, approval } = row;
                            const { data } = task;
                            const isActionable = ACTIONABLE.has(approval);
                            const hasEntries = Boolean(data.entries && data.entries.length > 0);
                            const syncError = data.sync?.status === 'error' ? data.sync : undefined;
                            const failedEntryKey = syncError?.entryKey;
                            const syncErrorMessage = syncError?.error;
                            const surveyVal = data.value?.survey?.value;
                            const intVal = data.value?.integration?.value;
                            const utrValueType = utrByCode.get(data.utrCode)?.valueType;
                            const showUnit = isNumericValueType(utrValueType);
                            const surveyUnit = showUnit ? (data.value?.survey?.unit ?? '') : '';
                            const intUnit = showUnit ? (data.value?.integration?.unit ?? '') : '';
                            const isExpanded = expandedRows.has(task.id);

                            return (
                              <React.Fragment key={task.id}>
                                <tr
                                  className={`align-middle data-review-row ${!isActionable ? 'opacity-50' : ''} ${hasEntries ? 'cursor-pointer' : ''}`}
                                  style={{ borderLeft: `3px solid ${getRowBorderColor(approval)}` }}
                                  onClick={hasEntries ? () => toggleExpand(task.id) : undefined}
                                >
                                  {selectMode && (
                                    <td className='ps-4 py-3' onClick={(e) => e.stopPropagation()}>
                                      {isActionable && (
                                        <input
                                          type='checkbox'
                                          className='form-check-input'
                                          checked={selectedIds.has(task.id)}
                                          onChange={() => toggleSelection(task.id)}
                                        />
                                      )}
                                    </td>
                                  )}
                                  <td className='py-3'>
                                    <div className='d-flex align-items-center gap-2'>
                                      {hasEntries && <i className={`fal fa-chevron-${isExpanded ? 'down' : 'right'} text-ThemeTextLight`} />}
                                      <div>
                                        <div className='d-flex align-items-center gap-2'>
                                          <span className='fw-bold'>{formatUtrCode(data.utrCode)}</span>
                                        </div>
                                        <div className='small text-ThemeTextLight'>
                                          {getUtrName(data.utrCode) ?? getGriSection(data.utrCode)}
                                          {data.surveyId && data.utrvId && (
                                            <a
                                              href={`${generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, { initiativeId: subsidiaryId, surveyId: data.surveyId })}/question/${data.utrvId}`}
                                              className='ms-2 text-ThemeTextMedium'
                                              target='_blank'
                                              rel='noopener noreferrer'
                                              onClick={(e) => e.stopPropagation()}
                                              title={`View ${QUESTION.SINGULAR}`}
                                            >
                                              <i className='fal fa-external-link' />
                                            </a>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className='py-3 text-end'>
                                    {hasEntries
                                      ? <span className='text-ThemeTextMedium'>{data.entries?.length} sub-{QUESTION.PLURAL}</span>
                                      : <TruncatedValue value={surveyVal} unit={surveyUnit} resolveLabel={(code) => getEntryLabel(data.utrCode, code)} />
                                    }
                                  </td>
                                  <td className='py-3 text-end text-primary'>
                                    {hasEntries
                                      ? null
                                      : <TruncatedValue value={intVal} unit={intUnit} resolveLabel={(code) => getEntryLabel(data.utrCode, code)} />
                                    }
                                  </td>
                                  <td className='py-3 text-end'>
                                    <DiffCell hasEntries={hasEntries} entries={data.entries} hasDiscrepancy={data.value?.hasDiscrepancy} surveyVal={surveyVal} intVal={intVal} />
                                  </td>
                                  <td className='py-3 text-end'>
                                    <StatusBadge status={data.sync?.status === 'synced' ? 'synced' : approval} />
                                  </td>
                                  <td className='py-3 text-end' onClick={(e) => e.stopPropagation()}>
                                    <div className='data-review-actions d-flex align-items-center justify-content-end gap-1'>
                                      {isActionable && !data.sync && !isCompleted && !isBulkSyncing && (
                                        <>
                                          {approval === RowApprovalStatus.Approved ? (
                                            <button
                                              className='btn btn-sm rounded-circle d-flex align-items-center justify-content-center text-primary'
                                              style={{ width: 28, height: 28 }}
                                              title={`Sync this ${QUESTION.SINGULAR}`}
                                              onClick={() => handleSyncTask(task.id, row.jobId)}
                                            >
                                              <i className='fal fa-cloud-upload' />
                                            </button>
                                          ) : (
                                            <span style={{ width: 28, height: 28 }} />
                                          )}
                                          <button
                                            className={`btn btn-sm rounded-circle d-flex align-items-center justify-content-center ${approval === RowApprovalStatus.Approved ? 'text-ThemeSuccessMedium background-ThemeSuccessExtralight' : 'text-ThemeTextLight'}`}
                                            style={{ width: 28, height: 28 }}
                                            title={hasEntries ? 'Approve all' : 'Approve'}
                                            disabled={approval === RowApprovalStatus.Approved}
                                            onClick={() => handleRowAction(task.id, row.jobId, RowApprovalStatus.Approved, approval)}
                                          >
                                            <i className='fal fa-check' />
                                          </button>
                                          <button
                                            className={`btn btn-sm rounded-circle d-flex align-items-center justify-content-center ${approval === RowApprovalStatus.Rejected ? 'text-ThemeDangerMedium background-ThemeDangerExtralight' : 'text-ThemeTextLight'}`}
                                            style={{ width: 28, height: 28 }}
                                            title={hasEntries ? 'Reject group' : 'Reject'}
                                            disabled={approval === RowApprovalStatus.Rejected}
                                            onClick={() => handleRowAction(task.id, row.jobId, RowApprovalStatus.Rejected, approval)}
                                          >
                                            <i className='fal fa-times' />
                                          </button>
                                        </>
                                      )}
                                      {data.sync?.status === 'synced' && (
                                        <i className='fal fa-check-circle text-ThemeSuccessMedium' title='Synced' />
                                      )}
                                      {data.sync?.status === 'error' && (
                                        <>
                                          <SimpleTooltip text={syncErrorMessage || 'Sync failed'}>
                                            <i className='fal fa-exclamation-circle text-ThemeDangerMedium' />
                                          </SimpleTooltip>
                                          {!isBulkSyncing && !isCompleted && (
                                            <SimpleTooltip text={`Retry sync — ${syncErrorMessage || 'Sync failed'}`}>
                                              <button
                                                className='btn btn-sm rounded-circle d-flex align-items-center justify-content-center text-ThemeDangerMedium'
                                                style={{ width: 28, height: 28 }}
                                                onClick={() => handleSyncTask(task.id, row.jobId)}
                                              >
                                                <i className='fal fa-redo' />
                                              </button>
                                            </SimpleTooltip>
                                          )}
                                        </>
                                      )}
                                      {data.utrvId && (
                                        <button
                                          className='btn btn-sm rounded-circle d-flex align-items-center justify-content-center text-ThemeTextLight'
                                          style={{ width: 28, height: 28 }}
                                          title='View history'
                                          onClick={() => { if (data.utrvId) { setProvenanceUtrvId({ utrvId: data.utrvId, utrCode: data.utrCode }); } }}
                                        >
                                          <i className='fal fa-clock-rotate-left' />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>

                                {hasEntries && isExpanded && data.entries?.map((entry) => {
                                  const entryKey = entry.optionCode ?? entry.key;
                                  const entryFailed = failedEntryKey !== undefined && entryKey === failedEntryKey;
                                  return (
                                  <tr
                                    key={`${task.id}-${entry.key}`}
                                    className='align-middle'
                                    style={{ borderLeft: `3px solid ${getRowBorderColor(approval)}` }}
                                  >
                                    {selectMode && <td></td>}
                                    <td className='py-3 ps-4'>
                                      <div className='d-flex align-items-center gap-1'>
                                        <span className='text-ThemeTextLight small'>└</span>
                                        <div>
                                          <div className='fw-bold'>{getEntryLabel(data.utrCode, entry.optionCode ?? entry.key)}</div>
                                          <div className='small text-ThemeTextLight'>{entry.optionCode ?? entry.key}</div>
                                          {entryFailed && syncErrorMessage && (
                                            <div className='small text-ThemeDangerMedium mt-1 text-break'>
                                              <i className='fal fa-exclamation-circle me-1' />
                                              {syncErrorMessage}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                    <td className='py-3 text-end'>{formatValue(entry.survey?.value, entry.survey?.unit)}</td>
                                    <td className='py-3 text-end text-primary'>{formatValue(entry.integration?.value, entry.integration?.unit)}</td>
                                    <td className='py-3 text-end'>
                                      <DiffBadge survey={entry.survey?.value} proposed={entry.integration?.value} hasDiscrepancy={entry.hasDiscrepancy} />
                                    </td>
                                    <td className='py-3 text-end'>
                                      <StatusBadge status={approval} />
                                    </td>
                                    <td></td>
                                  </tr>
                                  );
                                })}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                    </Collapse>
                  </DashboardSection>
                </React.Fragment>
              );
            })}


            {selectMode && (
              <div
                className='position-fixed bottom-0 start-0 end-0 py-3 text-ThemeTextWhite background-ThemeBgDarkest'
                style={{ zIndex: 100 }}
              >
                <div className='d-flex align-items-center justify-content-end gap-3 px-4'>
                  <span className='me-auto small'>
                    {selectedIds.size} of {filteredRows.filter((r) => ACTIONABLE.has(r.approval)).length} selected
                  </span>
                  <button
                    className='btn btn-outline-light'
                    disabled={selectedIds.size === 0}
                    onClick={() => handleBulkApproval(flatRows, RowApprovalStatus.Rejected)}
                  >
                    Reject Selected
                  </button>
                  <button
                    className='btn btn-outline-light'
                    disabled={selectedIds.size === 0}
                    onClick={() => handleBulkApproval(flatRows, RowApprovalStatus.Approved)}
                  >
                    Approve Selected
                  </button>
                </div>
              </div>
            )}


            {selectMode && <div style={{ height: 60 }} />}
          </>
        );
      }} />
      <ConfirmationModal
        isOpen={Boolean(refreshConfirm)}
        title='Refresh Snapshot Data'
        content={
          refreshConfirm?.hasSynced
            ? <>This will re-fetch integration data for <strong>{refreshConfirm.period}</strong> and regenerate all non-synced {QUESTION.PLURAL}. Synced {QUESTION.PLURAL} will be preserved. Any pending review decisions will be lost.</>
            : <>This will re-fetch integration data for <strong>{refreshConfirm?.period}</strong> and regenerate all {QUESTION.PLURAL}. Any pending review decisions will be lost.</>
        }
        handleCancel={() => setRefreshConfirm(null)}
        confirmButton={
          <button
            className='btn btn-warning'
            onClick={() => {
              if (refreshConfirm) {
                regeneratePending({ initiativeId, code, dataTransferId, jobId: refreshConfirm.jobId }).then((r) => handleMutationResult(r, 'Failed to refresh snapshot'));
              }
              setRefreshConfirm(null);
            }}
          >
            <i className='fal fa-refresh me-1' />Refresh
          </button>
        }
      />
      <ProvenanceModalForMetric provenanceUtrvId={provenanceUtrvId} utrByCode={utrByCode} onClose={() => setProvenanceUtrvId(null)} />
    </Dashboard>
  );
};
